import { useState, useMemo } from "react"
import {
  Dropdown,
  Spinner,
  Form,
  Button,
  Card,
  Image,
  Alert,
} from "react-bootstrap"
import {
  useAllMainSiteContent,
  useCreateMainSiteContent,
  useDeleteMainSiteContent,
  useUpdateMainSiteContent,
  useToggleMainSiteContentStatus,
} from "../../../api/main-site-content/main-site-content.hooks"
import DeleteModal from "../../components/DeleteModal"
import { useUploadFile } from "../../../api/fileUpload/fileUpload.hooks"
import { FiCopy } from "react-icons/fi"
import Pagination from "../../components/Pagination/Pagination"
import _ from "lodash"
import { standardizePageName } from "../../../utils/stringUtils"

const ByInterest = () => {
  const TYPE = "attractions-by-category"

  const defaultContentStructure = {
    heroSection: {
      isVisible: true,
      title: "",
      subtitle: "",
      backgroundImage: "",
    },
    landOfFaithAndHarmonySection: {
      isVisible: true,
      heading: "",
      description: "",
      quote: "",
      mainImage: "",
      sideImage: "",
      link: "",
    },
    exploreSpiritualSitesSection: {
      isVisible: true,
      heading: "",
      description: "",
      backgroundImage: "",
      link: "",
      sites: [
        {
          title: "",
          image: "",
          link: "",
        },
      ],
    },
    traditionsSection: {
      isVisible: true,
      traditions: [
        {
          image: "",
          city: "",
          title: "",
          mainDescription: "",
          subDescription: "",
          link: "",
        },
      ],
    },
    festivalsRalliesAndTourismSection: {
      isVisible: true,
      heading: "",
      description: "",
      link: "",
      colonialSites: [
        {
          title: "",
          city: "",
          description: "",
          image: "",
          link: "",
        },
      ],
    },
    ourHotelsAndResortsSection: {
      isVisible: true,
      heading: "",
      description: "",
      link: "",
      sikhSites: [
        {
          title: "",
          city: "",
          desc: "",
          image: "",
          link: "",
        },
      ],
    },
    majesticDarewarFortSection: {
      isVisible: true,
      heading: "",
      description: "",
      mainImage: "",
      link: "",
      sideImages: [{ img: "", name: "", link: "" }],
      featuredCard: {
        title: "",
        description: "",
        image: "",
        link: "",
      },
    },
    meta: {
      isVisible: true,
      title: "",
      description: "",
    },
  }

  const itemsPerPage = 10
  const [currentPage, setCurrentPage] = useState(0)
  const [searchTerm, setSearchTerm] = useState("")
  const [showFormView, setShowFormView] = useState(false)
  const [editItem, setEditItem] = useState(null)
  const [formData, setFormData] = useState({
    siteName: "",
    pageName: "",
    type: "",
    content: defaultContentStructure,
  })
  const [selectedItem, setSelectedItem] = useState(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [modalAction, setModalAction] = useState('delete') // 'delete' or 'toggle'
  const [alert, setAlert] = useState({
    show: false,
    message: "",
    variant: "info",
  })
  const [validationErrors, setValidationErrors] = useState({})

  const { upload, isLoading: isUploading } = useUploadFile()
  const { deleteContent, isLoading: isDeleting } = useDeleteMainSiteContent()
  const {
    content: apiContent,
    isLoading,
    refetch,
  } = useAllMainSiteContent({ type: TYPE })
  const { updateContent, isLoading: isUpdating } = useUpdateMainSiteContent()
  const { createContent, isLoading: isCreating } = useCreateMainSiteContent()
  const { toggleStatus, isLoading: isToggling } = useToggleMainSiteContentStatus()

  const showAlert = (message, variant = "info") => {
    setAlert({ show: true, message, variant })
    setTimeout(
      () => setAlert({ show: false, message: "", variant: "info" }),
      5000
    )
  }

  const handleCopy = async (text) => {
    try {
      await navigator.clipboard.writeText(text)
      showAlert("Copied to clipboard!", "success")
    } catch (err) {
      showAlert("Failed to copy!", "danger")
    }
  }

  const handleDelete = async () => {
    if (!selectedItem) return
    try {
      let success = false
      if (modalAction === 'delete') {
        success = await deleteContent(selectedItem._id)
        if (success) {
          showAlert("Item deleted successfully!", "success")
        }
      } else if (modalAction === 'toggle') {
        success = await toggleStatus(selectedItem._id)
        if (success) {
          showAlert("Status toggled successfully!", "success")
        }
      }
      
      if (success) {
        setShowDeleteModal(false)
        await refetch()
      }
    } catch {
      showAlert(`Failed to ${modalAction === 'delete' ? 'delete' : 'toggle status of'} item.`, "danger")
    }
  }

  const handleToggleStatus = async (item) => {
    setSelectedItem(item)
    setModalAction('toggle')
    setShowDeleteModal(true)
  }

  const openDeleteModal = (item) => {
    setSelectedItem(item)
    setModalAction('delete')
    setShowDeleteModal(true)
  }

  const validateForm = () => {
    const errors = {}

    // Validate page name
    if (!formData.pageName.trim()) {
      errors.pageName = "Page name is required"
    }

    // Validate each section
    Object.entries(formData.content).forEach(([section, fields]) => {
      if (fields.isVisible) {
        // Check if section has heading field and it's empty
        if (fields.heading !== undefined && !fields.heading.trim()) {
          errors[`${section}.heading`] =
            "Heading is required when section is visible"
        }

        // Check if section has description field and it's empty
        if (fields.description !== undefined && !fields.description.trim()) {
          errors[`${section}.description`] =
            "Description is required when section is visible"
        }

        // Check if section has title field and it's empty (for heroSection and meta)
        if (fields.title !== undefined && !fields.title.trim()) {
          errors[`${section}.title`] =
            "Title is required when section is visible"
        }
      }
    })

    setValidationErrors(errors)
    return Object.keys(errors).length === 0
  }

  const sanitizeContentData = (content) => {
    // Create a deep clone of the default structure
    const sanitizedContent = _.cloneDeep(defaultContentStructure)

    // Merge content while maintaining structure
    Object.keys(sanitizedContent).forEach((sectionKey) => {
      if (content[sectionKey]) {
        const section = content[sectionKey]

        // Merge basic fields
        Object.keys(sanitizedContent[sectionKey]).forEach((fieldKey) => {
          if (section[fieldKey] !== undefined) {
            if (Array.isArray(sanitizedContent[sectionKey][fieldKey])) {
              // Handle arrays - validate each item structure
              const defaultArrayItem = sanitizedContent[sectionKey][fieldKey][0]
              if (Array.isArray(section[fieldKey])) {
                sanitizedContent[sectionKey][fieldKey] = section[fieldKey].map(
                  (item) => {
                    if (typeof defaultArrayItem === "object" && defaultArrayItem !== null) {
                      // Ensure each array item has the correct structure
                      const sanitizedItem = {}
                      Object.keys(defaultArrayItem).forEach((itemKey) => {
                        sanitizedItem[itemKey] = item[itemKey] || defaultArrayItem[itemKey]
                      })
                      return sanitizedItem
                    }
                    return item
                  }
                )
              }
            } else if (
              typeof sanitizedContent[sectionKey][fieldKey] === "object" &&
              sanitizedContent[sectionKey][fieldKey] !== null &&
              !Array.isArray(sanitizedContent[sectionKey][fieldKey])
            ) {
              // Handle nested objects (like featuredCard)
              const defaultNestedObj = sanitizedContent[sectionKey][fieldKey]
              if (typeof section[fieldKey] === "object" && section[fieldKey] !== null) {
                const sanitizedNestedObj = {}
                Object.keys(defaultNestedObj).forEach((nestedKey) => {
                  sanitizedNestedObj[nestedKey] = section[fieldKey][nestedKey] || defaultNestedObj[nestedKey]
                })
                sanitizedContent[sectionKey][fieldKey] = sanitizedNestedObj
              }
            } else {
              // Handle primitive values
              sanitizedContent[sectionKey][fieldKey] = section[fieldKey]
            }
          }
        })
      }
    })

    return sanitizedContent
  }

  const handleSaveChanges = async () => {
    if (!validateForm()) {
      showAlert(
        "Please fill in all required fields for visible sections.",
        "danger"
      )
      return
    }

    try {
      // Sanitize content data before sending to API
      const sanitizedContent = sanitizeContentData(formData.content)
      
      const payload = {
        siteName: formData.siteName || "main-site",
        pageName: standardizePageName(formData.pageName), // Standardize page name
        type: TYPE,
        content: sanitizedContent,
      }

      let success = false
      if (editItem) {
        success = await updateContent(editItem._id, payload)
      } else {
        success = await createContent(payload)
      }

      if (success) {
        showAlert(
          editItem ? "Updated successfully!" : "Added successfully!",
          "success"
        )
        setShowFormView(false)
        setValidationErrors({})
        await refetch()
      }
    } catch (err) {
      console.error(err)
      showAlert("Operation failed.", "danger")
    }
  }

  const handleEdit = (item) => {
    setEditItem(item)

    // Sanitize existing content to match default structure
    const sanitizedContent = sanitizeContentData(item.content || {})

    setFormData({
      siteName: item.siteName || "main-site",
      pageName: item.pageName || "",
      type: item.type || TYPE,
      content: sanitizedContent,
    })

    setShowFormView(true)
  }

  const handleInputChange = (section, key, value) => {
    setFormData((prev) => ({
      ...prev,
      content: {
        ...prev.content,
        [section]: { ...prev.content[section], [key]: value },
      },
    }))
  }

  const handleFileUpload = async (section, key, file) => {
    try {
      const res = await upload(file)
      if (res?.url) handleInputChange(section, key, res.url)
    } catch (err) {
      showAlert("File upload failed.", "danger")
    }
  }

  const handleSitesChange = (index, field, value) => {
    const sites = [...formData.content.exploreSpiritualSitesSection.sites]
    sites[index][field] = value
    handleInputChange("exploreSpiritualSitesSection", "sites", sites)
  }

  const handleAddSite = () => {
    const sites = [...formData.content.exploreSpiritualSitesSection.sites]
    // if (sites.length >= 4) return
    sites.push({ title: "", image: "", link: "" })
    handleInputChange("exploreSpiritualSitesSection", "sites", sites)
  }

  const handleRemoveSite = (index) => {
    const sites = [...formData.content.exploreSpiritualSitesSection.sites]
    sites.splice(index, 1)
    handleInputChange("exploreSpiritualSitesSection", "sites", sites)
  }

  const handleSiteImageUpload = async (index, file) => {
    try {
      const res = await upload(file)
      if (res?.url) {
        const sites = [...formData.content.exploreSpiritualSitesSection.sites]
        sites[index].image = res.url
        handleInputChange("exploreSpiritualSitesSection", "sites", sites)
      }
    } catch {
      showAlert("Failed to upload image.", "danger")
    }
  }

  const handleTraditionsChange = (index, field, value) => {
    const traditions = [...formData.content.traditionsSection.traditions]
    traditions[index][field] = value
    handleInputChange("traditionsSection", "traditions", traditions)
  }

  const handleAddTradition = () => {
    const traditions = [...formData.content.traditionsSection.traditions]
    // if (traditions.length >= 4) return
    traditions.push({
      image: "",
      city: "",
      title: "",
      mainDescription: "",
      subDescription: "",
      link: "",
    })
    handleInputChange("traditionsSection", "traditions", traditions)
  }

  const handleRemoveTradition = (index) => {
    const traditions = [...formData.content.traditionsSection.traditions]
    traditions.splice(index, 1)
    handleInputChange("traditionsSection", "traditions", traditions)
  }

  const handleTraditionImageUpload = async (index, file) => {
    try {
      const res = await upload(file)
      if (res?.url) {
        const traditions = [...formData.content.traditionsSection.traditions]
        traditions[index].image = res.url
        handleInputChange("traditionsSection", "traditions", traditions)
      }
    } catch {
      showAlert("Failed to upload image.", "danger")
    }
  }

  const handleColonialSitesChange = (index, field, value) => {
    const colonialSites = [
      ...formData.content.festivalsRalliesAndTourismSection.colonialSites,
    ]
    colonialSites[index][field] = value
    handleInputChange(
      "festivalsRalliesAndTourismSection",
      "colonialSites",
      colonialSites
    )
  }

  const handleAddColonialSite = () => {
    const colonialSites = [
      ...formData.content.festivalsRalliesAndTourismSection.colonialSites,
    ]
    // if (colonialSites.length >= 4) return
    colonialSites.push({ title: "", city: "", description: "", image: "", link: "" })
    handleInputChange(
      "festivalsRalliesAndTourismSection",
      "colonialSites",
      colonialSites
    )
  }

  const handleRemoveColonialSite = (index) => {
    const colonialSites = [
      ...formData.content.festivalsRalliesAndTourismSection.colonialSites,
    ]
    colonialSites.splice(index, 1)
    handleInputChange(
      "festivalsRalliesAndTourismSection",
      "colonialSites",
      colonialSites
    )
  }

  const handleColonialSiteImageUpload = async (index, file) => {
    try {
      const res = await upload(file)
      if (res?.url) {
        const colonialSites = [
          ...formData.content.festivalsRalliesAndTourismSection.colonialSites,
        ]
        colonialSites[index].image = res.url
        handleInputChange(
          "festivalsRalliesAndTourismSection",
          "colonialSites",
          colonialSites
        )
      }
    } catch {
      showAlert("Failed to upload image.", "danger")
    }
  }

  const handleSikhSitesChange = (index, field, value) => {
    const sikhSites = [...formData.content.ourHotelsAndResortsSection.sikhSites]
    sikhSites[index][field] = value
    handleInputChange("ourHotelsAndResortsSection", "sikhSites", sikhSites)
  }

  const handleAddSikhSite = () => {
    const sikhSites = [...formData.content.ourHotelsAndResortsSection.sikhSites]
    // if (sikhSites.length >= 4) return
    sikhSites.push({ title: "", city: "", desc: "", image: "", link: "" })
    handleInputChange("ourHotelsAndResortsSection", "sikhSites", sikhSites)
  }

  const handleRemoveSikhSite = (index) => {
    const sikhSites = [...formData.content.ourHotelsAndResortsSection.sikhSites]
    sikhSites.splice(index, 1)
    handleInputChange("ourHotelsAndResortsSection", "sikhSites", sikhSites)
  }

  const handleSikhSiteImageUpload = async (index, file) => {
    try {
      const res = await upload(file)
      if (res?.url) {
        const sikhSites = [
          ...formData.content.ourHotelsAndResortsSection.sikhSites,
        ]
        sikhSites[index].image = res.url
        handleInputChange("ourHotelsAndResortsSection", "sikhSites", sikhSites)
      }
    } catch {
      showAlert("Failed to upload image.", "danger")
    }
  }

  const handleSideImagesChange = (index, field, value) => {
    const sideImages = [...formData.content.majesticDarewarFortSection.sideImages]
    sideImages[index][field] = value
    handleInputChange("majesticDarewarFortSection", "sideImages", sideImages)
  }

  const handleAddSideImage = () => {
    const sideImages = [...formData.content.majesticDarewarFortSection.sideImages]
    // if (sideImages.length >= 4) return
    sideImages.push({ img: "", name: "", link: "" })
    handleInputChange("majesticDarewarFortSection", "sideImages", sideImages)
  }

  const handleRemoveSideImage = (index) => {
    const sideImages = [...formData.content.majesticDarewarFortSection.sideImages]
    sideImages.splice(index, 1)
    handleInputChange("majesticDarewarFortSection", "sideImages", sideImages)
  }

  const handleSideImageUpload = async (index, file) => {
    try {
      const res = await upload(file)
      if (res?.url) {
        const sideImages = [...formData.content.majesticDarewarFortSection.sideImages]
        sideImages[index].img = res.url
        handleInputChange("majesticDarewarFortSection", "sideImages", sideImages)
      }
    } catch {
      showAlert("Failed to upload image.", "danger")
    }
  }

  const handleFeaturedCardChange = (field, value) => {
    const featuredCard = { ...formData.content.majesticDarewarFortSection.featuredCard }
    featuredCard[field] = value
    handleInputChange("majesticDarewarFortSection", "featuredCard", featuredCard)
  }

  const handleFeaturedCardImageUpload = async (file) => {
    try {
      const res = await upload(file)
      if (res?.url) {
        handleFeaturedCardChange("image", res.url)
      }
    } catch {
      showAlert("Failed to upload image.", "danger")
    }
  }

  const tableData = useMemo(() => {
    if (!Array.isArray(apiContent)) return []
    return apiContent.map((item) => ({
      id: item._id || item.id,
      siteName: item.siteName || "-",
      pageName: item.pageName || "-",
      type: item.type || "-",
      isActive: item.isActive,
      fullItem: item,
    }))
  }, [apiContent])

  const filtered = tableData.filter((x) =>
    [x.siteName, x.pageName, x.type]
      .join(" ")
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  )

  const totalPages = Math.ceil(filtered.length / itemsPerPage) || 1
  const paginated = filtered.slice(
    currentPage * itemsPerPage,
    (currentPage + 1) * itemsPerPage
  )

  return (
    <div className='col-12'>
      {alert.show && (
        <Alert
          variant={alert.variant}
          dismissible
          onClose={() =>
            setAlert({ show: false, message: "", variant: "info" })
          }
          className='mb-3'
        >
          {alert.message}
        </Alert>
      )}

      {!showFormView ? (
        <div className='card'>
          <div className='card-header d-flex justify-content-between align-items-center'>
            <h4 className='card-title m-0'>By Region</h4>
            <div className='d-flex gap-2'>
              <input
                type='text'
                className='form-control w-auto'
                placeholder='Search content'
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <Button
                onClick={() => {
                  setEditItem(null)
                  setFormData({
                    siteName: "main-site",
                    pageName: "",
                    type: TYPE,
                    content: defaultContentStructure,
                  })
                  setShowFormView(true)
                }}
              >
                + Add
              </Button>
            </div>
          </div>

          <div className='card-body'>
            {isLoading ? (
              <div className='d-flex justify-content-center py-5'>
                <Spinner animation='border' />
              </div>
            ) : (
              <>
                <div className='table-responsive'>
                  <table className='table table-striped'>
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Site</th>
                        <th>Page</th>
                        <th>Url</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginated.map((item, i) => (
                        <tr key={item.id}>
                          <td>{currentPage * itemsPerPage + i + 1}</td>
                          <td>{item.siteName}</td>
                          <td>{item.pageName}</td>
                          <td className='d-flex align-items-center gap-2'>
                            <span>/attractions/{item.pageName}</span>
                            <Button
                              variant='link'
                              onClick={() =>
                                handleCopy(`/attractions/${item.pageName}`)
                              }
                              title='Copy URL'
                            >
                              <FiCopy />
                            </Button>
                          </td>
                          <td>
                            <span className={`badge ${item.isActive ? 'bg-success' : 'bg-secondary'}`}>
                              {item.isActive ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td>
                            <Dropdown>
                              <Dropdown.Toggle variant='link'>
                                &#x2022;&#x2022;&#x2022;
                              </Dropdown.Toggle>
                              <Dropdown.Menu align='end'>
                                <Dropdown.Item
                                  onClick={() => handleEdit(item.fullItem)}
                                >
                                  Edit
                                </Dropdown.Item>
                                <Dropdown.Item
                                  onClick={() => handleToggleStatus(item.fullItem)}
                                >
                                  {item.fullItem.isActive ? 'Deactivate' : 'Activate'}
                                </Dropdown.Item>
                                <Dropdown.Item
                                  onClick={() => openDeleteModal(item.fullItem)}
                                  className="text-danger"
                                >
                                  Delete
                                </Dropdown.Item>
                              </Dropdown.Menu>
                            </Dropdown>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  itemsPerPage={itemsPerPage}
                  totalItems={filtered.length}
                  onPageChange={setCurrentPage}
                />

                {filtered.length === 0 && !isLoading && (
                  <div className="text-center py-4">
                    <p className="text-muted">No content found</p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      ) : (
        <div className='card p-4'>
          <div className='d-flex align-items-center mb-4'>
            <Button
              variant='link'
              className='p-0 me-3'
              onClick={() => setShowFormView(false)}
            >
              ← Back
            </Button>
            <h4 className='m-0'>
              {editItem ? "Edit Content" : "Add New Content"}
            </h4>
          </div>

          <Form>
            <Form.Group className='mb-3'>
              <Form.Label>
                Page Name <span className='text-danger'>*</span>
              </Form.Label>
              <Form.Control
                type='text'
                value={formData.pageName}
                onChange={(e) => {
                  setFormData({ ...formData, pageName: e.target.value })
                  if (validationErrors.pageName) {
                    setValidationErrors((prev) => ({
                      ...prev,
                      pageName: undefined,
                    }))
                  }
                }}
                isInvalid={!!validationErrors.pageName}
              />
              {formData.pageName && (
                <Form.Text className="text-muted">
                  URL Preview: /attractions/{standardizePageName(formData.pageName)}
                </Form.Text>
              )}
              {validationErrors.pageName && (
                <Form.Control.Feedback type='invalid'>
                  {validationErrors.pageName}
                </Form.Control.Feedback>
              )}
            </Form.Group>

            {Object.entries(formData.content).map(([section, fields]) => (
              <Card key={section} className='mb-4 shadow'>
                <Card.Header className='bg-neutral-gray text-dark d-flex justify-content-between align-items-center'>
                  <h5 className='mb-0 text-capitalize text-dark'>
                    {section === "heroSection" && "Hero Section"}
                    {section === "landOfFaithAndHarmonySection" &&
                      "Section 1"}
                    {section === "exploreSpiritualSitesSection" &&
                      "Section 2"}
                    {section === "traditionsSection" && "Section 3"}
                    {section === "festivalsRalliesAndTourismSection" &&
                      "Section 4"}
                    {section === "ourHotelsAndResortsSection" &&
                      "Section 5"}
                    {section === "majesticDarewarFortSection" &&
                      "Section 6"}
                    {section === "meta" && "Meta Information"}
                  </h5>
                  <Form.Check
                    type='checkbox'
                    label='Visible'
                    checked={fields.isVisible}
                    onChange={(e) =>
                      handleInputChange(section, "isVisible", e.target.checked)
                    }
                    className='mb-0'
                  />
                </Card.Header>
                {fields.isVisible && (
                  <Card.Body>
                    <>
                      {section === "exploreSpiritualSitesSection" ? (
                        <>
                          <Form.Control
                            className='mb-3'
                            placeholder={`Heading ${
                              fields.isVisible ? "*" : ""
                            }`}
                            value={fields.heading}
                            onChange={(e) => {
                              handleInputChange(
                                section,
                                "heading",
                                e.target.value
                              )
                              if (validationErrors[`${section}.heading`]) {
                                setValidationErrors((prev) => ({
                                  ...prev,
                                  [`${section}.heading`]: undefined,
                                }))
                              }
                            }}
                            isInvalid={!!validationErrors[`${section}.heading`]}
                          />
                          {validationErrors[`${section}.heading`] && (
                            <div className='invalid-feedback d-block mb-2'>
                              {validationErrors[`${section}.heading`]}
                            </div>
                          )}
                          <Form.Control
                            as='textarea'
                            rows={2}
                            placeholder={`Description ${
                              fields.isVisible ? "*" : ""
                            }`}
                            value={fields.description}
                            onChange={(e) => {
                              handleInputChange(
                                section,
                                "description",
                                e.target.value
                              )
                              if (validationErrors[`${section}.description`]) {
                                setValidationErrors((prev) => ({
                                  ...prev,
                                  [`${section}.description`]: undefined,
                                }))
                              }
                            }}
                            className='mb-4'
                            isInvalid={
                              !!validationErrors[`${section}.description`]
                            }
                          />
                          {validationErrors[`${section}.description`] && (
                            <div className='invalid-feedback d-block mb-2'>
                              {validationErrors[`${section}.description`]}
                            </div>
                          )}

                          <Form.Control
                            className='mb-2'
                            placeholder='Link (Optional)'
                            value={fields.link}
                            onChange={(e) =>
                              handleInputChange(section, "link", e.target.value)
                            }
                          />

                          <Form.Label>Background Image</Form.Label>
                          <Form.Control
                            type='file'
                            accept='image/*'
                            onChange={(e) =>
                              e.target.files?.[0] &&
                              handleFileUpload(
                                section,
                                "backgroundImage",
                                e.target.files[0]
                              )
                            }
                          />
                          {fields.backgroundImage && (
                            <div className='mt-2 mb-3'>
                              <Image
                                src={fields.backgroundImage}
                                thumbnail
                                style={{ maxWidth: "200px" }}
                              />
                            </div>
                          )}

                          <h6 className='fw-bold'>Sites</h6>
                          {fields.sites.map((site, idx) => (
                            <div
                              key={idx}
                              className='border rounded p-3 mb-3 bg-light-subtle'
                            >
                              <Form.Control
                                className='mb-2'
                                placeholder='Title'
                                value={site.title}
                                onChange={(e) =>
                                  handleSitesChange(
                                    idx,
                                    "title",
                                    e.target.value
                                  )
                                }
                              />

                              <Form.Control
                                className='mb-2'
                                placeholder='Link (Optional)'
                                value={site.link}
                                onChange={(e) =>
                                  handleSitesChange(
                                    idx,
                                    "link",
                                    e.target.value
                                  )
                                }
                              />

                              <Form.Label>Image</Form.Label>
                              <Form.Control
                                type='file'
                                accept='image/*'
                                onChange={(e) =>
                                  e.target.files?.[0] &&
                                  handleSiteImageUpload(idx, e.target.files[0])
                                }
                              />
                              {isUploading && (
                                <div className='text-muted small mt-1'>
                                  Uploading...
                                </div>
                              )}
                              {site.image && (
                                <div className='mt-2'>
                                  <Image
                                    src={site.image}
                                    thumbnail
                                    style={{ maxWidth: "200px" }}
                                  />
                                </div>
                              )}
                              <div className='text-end mt-2'>
                                <Button
                                  variant='outline-danger'
                                  size='sm'
                                  onClick={() => handleRemoveSite(idx)}
                                >
                                  Remove
                                </Button>
                              </div>
                            </div>
                          ))}
                          <Button
                            variant='outline-primary'
                            size='sm'
                            onClick={handleAddSite}
                            // disabled={fields.sites.length >= 4}
                          >
                            + Add Site
                          </Button>
                        </>
                      ) : section === "traditionsSection" ? (
                        <>
                          {/* <Form.Control
                            className='mb-2'
                            placeholder='Link (Optional)'
                            value={fields.link}
                            onChange={(e) =>
                              handleInputChange(section, "link", e.target.value)
                            }
                          /> */}

                          <h6 className='fw-bold'>Traditions</h6>
                          {fields.traditions.map((tradition, idx) => (
                            <div
                              key={idx}
                              className='border rounded p-3 mb-3 bg-light-subtle'
                            >
                              <Form.Control
                                className='mb-2'
                                placeholder='City'
                                value={tradition.city}
                                onChange={(e) =>
                                  handleTraditionsChange(
                                    idx,
                                    "city",
                                    e.target.value
                                  )
                                }
                              />
                              <Form.Control
                                className='mb-2'
                                placeholder='Title'
                                value={tradition.title}
                                onChange={(e) =>
                                  handleTraditionsChange(
                                    idx,
                                    "title",
                                    e.target.value
                                  )
                                }
                              />
                              <Form.Control
                                as='textarea'
                                rows={2}
                                placeholder='Main Description'
                                value={tradition.mainDescription}
                                onChange={(e) =>
                                  handleTraditionsChange(
                                    idx,
                                    "mainDescription",
                                    e.target.value
                                  )
                                }
                                className='mb-2'
                              />
                              <Form.Control
                                as='textarea'
                                rows={2}
                                placeholder='Sub Description'
                                value={tradition.subDescription}
                                onChange={(e) =>
                                  handleTraditionsChange(
                                    idx,
                                    "subDescription",
                                    e.target.value
                                  )
                                }
                                className='mb-2'
                              />

                              <Form.Control
                                className='mb-2'
                                placeholder='Link (Optional)'
                                value={tradition.link}
                                onChange={(e) =>
                                  handleTraditionsChange(
                                    idx,
                                    "link",
                                    e.target.value
                                  )
                                }
                              />

                              <Form.Label>Image</Form.Label>
                              <Form.Control
                                type='file'
                                accept='image/*'
                                onChange={(e) =>
                                  e.target.files?.[0] &&
                                  handleTraditionImageUpload(
                                    idx,
                                    e.target.files[0]
                                  )
                                }
                              />
                              {isUploading && (
                                <div className='text-muted small mt-1'>
                                  Uploading...
                                </div>
                              )}
                              {tradition.image && (
                                <div className='mt-2'>
                                  <Image
                                    src={tradition.image}
                                    thumbnail
                                    style={{ maxWidth: "200px" }}
                                  />
                                </div>
                              )}
                              <div className='text-end mt-2'>
                                <Button
                                  variant='outline-danger'
                                  size='sm'
                                  onClick={() => handleRemoveTradition(idx)}
                                >
                                  Remove
                                </Button>
                              </div>
                            </div>
                          ))}
                          <Button
                            variant='outline-primary'
                            size='sm'
                            onClick={handleAddTradition}
                            // disabled={fields.traditions.length >= 4}
                          >
                            + Add Tradition
                          </Button>
                        </>
                      ) : section === "festivalsRalliesAndTourismSection" ? (
                        <>
                          <Form.Control
                            className='mb-3'
                            placeholder={`Heading ${
                              fields.isVisible ? "*" : ""
                            }`}
                            value={fields.heading}
                            onChange={(e) => {
                              handleInputChange(
                                section,
                                "heading",
                                e.target.value
                              )
                              if (validationErrors[`${section}.heading`]) {
                                setValidationErrors((prev) => ({
                                  ...prev,
                                  [`${section}.heading`]: undefined,
                                }))
                              }
                            }}
                            isInvalid={!!validationErrors[`${section}.heading`]
                            }
                          />
                          {validationErrors[`${section}.heading`] && (
                            <div className='invalid-feedback d-block mb-2'>
                              {validationErrors[`${section}.heading`]
                              }
                            </div>
                          )}
                          <Form.Control
                            as='textarea'
                            rows={2}
                            placeholder={`Description ${
                              fields.isVisible ? "*" : ""
                            }`}
                            value={fields.description}
                            onChange={(e) => {
                              handleInputChange(
                                section,
                                "description",
                                e.target.value
                              )
                              if (validationErrors[`${section}.description`]) {
                                setValidationErrors((prev) => ({
                                  ...prev,
                                  [`${section}.description`]: undefined,
                                }))
                              }
                            }}
                            className='mb-4'
                            isInvalid={
                              !!validationErrors[`${section}.description`]
                            }
                          />
                          {validationErrors[`${section}.description`] && (
                            <div className='invalid-feedback d-block mb-2'>
                              {validationErrors[`${section}.description`]}
                            </div>
                          )}

                          <Form.Control
                            className='mb-2'
                            placeholder='Link (Optional)'
                            value={fields.link}
                            onChange={(e) =>
                              handleInputChange(section, "link", e.target.value)
                            }
                          />

                          <h6 className='fw-bold'>Colonial Sites</h6>
                          {fields.colonialSites.map((site, idx) => (
                            <div
                              key={idx}
                              className='border rounded p-3 mb-3 bg-light-subtle'
                            >
                              <Form.Control
                                className='mb-2'
                                placeholder='Title'
                                value={site.title}
                                onChange={(e) =>
                                  handleColonialSitesChange(
                                    idx,
                                    "title",
                                    e.target.value
                                  )
                                }
                              />
                              <Form.Control
                                className='mb-2'
                                placeholder='City'
                                value={site.city}
                                onChange={(e) =>
                                  handleColonialSitesChange(
                                    idx,
                                    "city",
                                    e.target.value
                                  )
                                }
                              />
                              <Form.Control
                                as='textarea'
                                rows={2}
                                placeholder='Description'
                                value={site.description}
                                onChange={(e) =>
                                  handleColonialSitesChange(
                                    idx,
                                    "description",
                                    e.target.value
                                  )
                                }
                                className='mb-2'
                              />

                              <Form.Control
                                className='mb-2'
                                placeholder='Link (Optional)'
                                value={site.link}
                                onChange={(e) =>
                                  handleColonialSitesChange(
                                    idx,
                                    "link",
                                    e.target.value
                                  )
                                }
                              />

                              <Form.Label>Image</Form.Label>
                              <Form.Control
                                type='file'
                                accept='image/*'
                                onChange={(e) =>
                                  e.target.files?.[0] &&
                                  handleColonialSiteImageUpload(
                                    idx,
                                    e.target.files[0]
                                  )
                                }
                              />
                              {isUploading && (
                                <div className='text-muted small mt-1'>
                                  Uploading...
                                </div>
                              )}
                              {site.image && (
                                <div className='mt-2'>
                                  <Image
                                    src={site.image}
                                    thumbnail
                                    style={{ maxWidth: "200px" }}
                                  />
                                </div>
                              )}
                              <div className='text-end mt-2'>
                                <Button
                                  variant='outline-danger'
                                  size='sm'
                                  onClick={() => handleRemoveColonialSite(idx)}
                                >
                                  Remove
                                </Button>
                              </div>
                            </div>
                          ))}
                          <Button
                            variant='outline-primary'
                            size='sm'
                            onClick={handleAddColonialSite}
                            // disabled={fields.colonialSites.length >= 4}
                          >
                            + Add Colonial Site
                          </Button>
                        </>
                      ) : section === "ourHotelsAndResortsSection" ? (
                        <>
                          <Form.Control
                            className='mb-3'
                            placeholder={`Heading ${
                              fields.isVisible ? "*" : ""
                            }`}
                            value={fields.heading}
                            onChange={(e) => {
                              handleInputChange(
                                section,
                                "heading",
                                e.target.value
                              )
                              if (validationErrors[`${section}.heading`]) {
                                setValidationErrors((prev) => ({
                                  ...prev,
                                  [`${section}.heading`]: undefined,
                                }))
                              }
                            }}
                            isInvalid={!!validationErrors[`${section}.heading`]}
                          />
                          {validationErrors[`${section}.heading`] && (
                            <div className='invalid-feedback d-block mb-2'>
                              {validationErrors[`${section}.heading`]}
                            </div>
                          )}
                          <Form.Control
                            as='textarea'
                            rows={2}
                            placeholder={`Description ${
                              fields.isVisible ? "*" : ""
                            }`}
                            value={fields.description}
                            onChange={(e) => {
                              handleInputChange(
                                section,
                                "description",
                                e.target.value
                              )
                              if (validationErrors[`${section}.description`]) {
                                setValidationErrors((prev) => ({
                                  ...prev,
                                  [`${section}.description`]: undefined,
                                }))
                              }
                            }}
                            className='mb-4'
                            isInvalid={
                              !!validationErrors[`${section}.description`]
                            }
                          />
                          {validationErrors[`${section}.description`] && (
                            <div className='invalid-feedback d-block mb-2'>
                              {validationErrors[`${section}.description`]}
                            </div>
                          )}

                          <Form.Control
                            className='mb-2'
                            placeholder='Link (Optional)'
                            value={fields.link}
                            onChange={(e) =>
                              handleInputChange(section, "link", e.target.value)
                            }
                          />

                          <h6 className='fw-bold'>Sikh Sites</h6>
                          {fields.sikhSites.map((site, idx) => (
                            <div
                              key={idx}
                              className='border rounded p-3 mb-3 bg-light-subtle'
                            >
                              <Form.Control
                                className='mb-2'
                                placeholder='Title'
                                value={site.title}
                                onChange={(e) =>
                                  handleSikhSitesChange(
                                    idx,
                                    "title",
                                    e.target.value
                                  )
                                }
                              />
                              <Form.Control
                                className='mb-2'
                                placeholder='City'
                                value={site.city}
                                onChange={(e) =>
                                  handleSikhSitesChange(
                                    idx,
                                    "city",
                                    e.target.value
                                  )
                                }
                              />
                              <Form.Control
                                as='textarea'
                                rows={2}
                                placeholder='Description'
                                value={site.desc}
                                onChange={(e) =>
                                  handleSikhSitesChange(
                                    idx,
                                    "desc",
                                    e.target.value
                                  )
                                }
                                className='mb-2'
                              />

                              <Form.Control
                                className='mb-2'
                                placeholder='Link (Optional)'
                                value={site.link}
                                onChange={(e) =>
                                  handleSikhSitesChange(
                                    idx,
                                    "link",
                                    e.target.value
                                  )
                                }
                              />

                              <Form.Label>Image</Form.Label>
                              <Form.Control
                                type='file'
                                accept='image/*'
                                onChange={(e) =>
                                  e.target.files?.[0] &&
                                  handleSikhSiteImageUpload(
                                    idx,
                                    e.target.files[0]
                                  )
                                }
                              />
                              {isUploading && (
                                <div className='text-muted small mt-1'>
                                  Uploading...
                                </div>
                              )}
                              {site.image && (
                                <div className='mt-2'>
                                  <Image
                                    src={site.image}
                                    thumbnail
                                    style={{ maxWidth: "200px" }}
                                  />
                                </div>
                              )}
                              <div className='text-end mt-2'>
                                <Button
                                  variant='outline-danger'
                                  size='sm'
                                  onClick={() => handleRemoveSikhSite(idx)}
                                >
                                  Remove
                                </Button>
                              </div>
                            </div>
                          ))}
                          <Button
                            variant='outline-primary'
                            size='sm'
                            onClick={handleAddSikhSite}
                            // disabled={fields.sikhSites.length >= 4}
                          >
                            + Add Sikh Site
                          </Button>
                        </>
                      ) : section === "majesticDarewarFortSection" ? (
                        <>
                          <Form.Control
                            className='mb-3'
                            placeholder={`Heading ${
                              fields.isVisible ? "*" : ""
                            }`}
                            value={fields.heading}
                            onChange={(e) => {
                              handleInputChange(
                                section,
                                "heading",
                                e.target.value
                              )
                              if (validationErrors[`${section}.heading`]) {
                                setValidationErrors((prev) => ({
                                  ...prev,
                                  [`${section}.heading`]: undefined,
                                }))
                              }
                            }}
                            isInvalid={!!validationErrors[`${section}.heading`]
                            }
                          />
                          {validationErrors[`${section}.heading`] && (
                            <div className='invalid-feedback d-block mb-2'>
                              {validationErrors[`${section}.heading`]
                              }
                            </div>
                          )}
                          <Form.Control
                            as='textarea'
                            rows={2}
                            placeholder={`Description ${
                              fields.isVisible ? "*" : ""
                            }`}
                            value={fields.description}
                            onChange={(e) => {
                              handleInputChange(
                                section,
                                "description",
                                e.target.value
                              )
                              if (validationErrors[`${section}.description`]) {
                                setValidationErrors((prev) => ({
                                  ...prev,
                                  [`${section}.description`]: undefined,
                                }))
                              }
                            }}
                            className='mb-4'
                            isInvalid={
                              !!validationErrors[`${section}.description`]
                            }
                          />
                          {validationErrors[`${section}.description`] && (
                            <div className='invalid-feedback d-block mb-2'>
                              {validationErrors[`${section}.description`]}
                            </div>
                          )}

                          <Form.Label>Main Image</Form.Label>
                          <Form.Control
                            type='file'
                            accept='image/*'
                            onChange={(e) =>
                              e.target.files?.[0] &&
                              handleFileUpload(
                                section,
                                "mainImage",
                                e.target.files[0]
                              )
                            }
                            className='mb-2'
                          />
                          {fields.mainImage && (
                            <div className='mt-2 mb-3'>
                              <Image
                                src={fields.mainImage}
                                thumbnail
                                style={{ maxWidth: "200px" }}
                              />
                            </div>
                          )}

                          <Form.Control
                            className='mb-4'
                            placeholder='Link (Optional)'
                            value={fields.link}
                            onChange={(e) =>
                              handleInputChange(section, "link", e.target.value)
                            }
                          />

                          <h6 className='fw-bold'>Side Images</h6>
                          {fields.sideImages.map((sideImg, idx) => (
                            <div
                              key={idx}
                              className='border rounded p-3 mb-3 bg-light-subtle'
                            >
                              <Form.Control
                                className='mb-2'
                                placeholder='Name'
                                value={sideImg.name}
                                onChange={(e) =>
                                  handleSideImagesChange(
                                    idx,
                                    "name",
                                    e.target.value
                                  )
                                }
                              />

                              <Form.Control
                                className='mb-2'
                                placeholder='Link (Optional)'
                                value={sideImg.link || ""}
                                onChange={(e) =>
                                  handleSideImagesChange(
                                    idx,
                                    "link",
                                    e.target.value
                                  )
                                }
                              />

                              <Form.Label>Image</Form.Label>
                              <Form.Control
                                type='file'
                                accept='image/*'
                                onChange={(e) =>
                                  e.target.files?.[0] &&
                                  handleSideImageUpload(idx, e.target.files[0])
                                }
                              />
                              {isUploading && (
                                <div className='text-muted small mt-1'>
                                  Uploading...
                                </div>
                              )}
                              {sideImg.img && (
                                <div className='mt-2'>
                                  <Image
                                    src={sideImg.img}
                                    thumbnail
                                    style={{ maxWidth: "200px" }}
                                  />
                                </div>
                              )}
                              <div className='text-end mt-2'>
                                <Button
                                  variant='outline-danger'
                                  size='sm'
                                  onClick={() => handleRemoveSideImage(idx)}
                                >
                                  Remove
                                </Button>
                              </div>
                            </div>
                          ))}
                          <Button
                            variant='outline-primary'
                            size='sm'
                            onClick={handleAddSideImage}
                            // disabled={fields.sideImages.length >= 4}
                            className='mb-4'
                          >
                            + Add Side Image
                          </Button>

                          <h6 className='fw-bold'>Featured Card</h6>
                          <div className='border rounded p-3 mb-3 bg-light-subtle'>
                            <Form.Control
                              className='mb-2'
                              placeholder='Title'
                              value={fields.featuredCard.title}
                              onChange={(e) =>
                                handleFeaturedCardChange("title", e.target.value)
                              }
                            />
                            <Form.Control
                              as='textarea'
                              rows={2}
                              placeholder='Description'
                              value={fields.featuredCard.description}
                              onChange={(e) =>
                                handleFeaturedCardChange("description", e.target.value)
                              }
                              className='mb-2'
                            />
                            <Form.Control
                              className='mb-2'
                              placeholder='Link (Optional)'
                              value={fields.featuredCard.link || ""}
                              onChange={(e) =>
                                handleFeaturedCardChange("link", e.target.value)
                              }
                            />

                            <Form.Label>Image</Form.Label>
                            <Form.Control
                              type='file'
                              accept='image/*'
                              onChange={(e) =>
                                e.target.files?.[0] &&
                                handleFeaturedCardImageUpload(e.target.files[0])
                              }
                            />
                            {isUploading && (
                              <div className='text-muted small mt-1'>
                                Uploading...
                              </div>
                            )}
                            {fields.featuredCard.image && (
                              <div className='mt-2'>
                                <Image
                                  src={fields.featuredCard.image}
                                  thumbnail
                                  style={{ maxWidth: "200px" }}
                                />
                              </div>
                            )}
                          </div>
                        </>
                      ) : (
                        Object.entries(fields).map(([key, value]) => {
                          if (key === "isVisible") return null // Skip isVisible as it's now in header

                          const isImageField =
                            key.toLowerCase().includes("image") ||
                            key.toLowerCase().includes("thumbnail")
                          const isRequiredField =
                            fields.isVisible &&
                            (key === "heading" ||
                              key === "description" ||
                              key === "title")

                          if (typeof value === "boolean") {
                            return (
                              <Form.Check
                                key={key}
                                type='checkbox'
                                label={key}
                                checked={value}
                                onChange={(e) =>
                                  handleInputChange(
                                    section,
                                    key,
                                    e.target.checked
                                  )
                                }
                                className='mb-3'
                              />
                            )
                          } else if (isImageField) {
                            return (
                              <div key={key} className='mb-3'>
                                <Form.Label className='text-capitalize'>
                                  {key}
                                </Form.Label>
                                <Form.Control
                                  type='file'
                                  accept='image/*'
                                  onChange={(e) =>
                                    e.target.files?.[0] &&
                                    handleFileUpload(
                                      section,
                                      key,
                                      e.target.files[0]
                                    )
                                  }
                                />
                                {isUploading && (
                                  <div className='text-muted small mt-1'>
                                    Uploading...
                                  </div>
                                )}
                                {value && (
                                  <div className='mt-2'>
                                    <Image
                                      src={value}
                                      alt='Preview'
                                      thumbnail
                                      style={{ maxWidth: "200px" }}
                                    />
                                  </div>
                                )}
                              </div>
                            )
                          } else if (key === "description" || key === "quote") {
                            return (
                              <Form.Group key={key} className='mb-3'>
                                <Form.Label className='text-capitalize'>
                                  {key}{" "}
                                  {isRequiredField && (
                                    <span className='text-danger'>*</span>
                                  )}
                                </Form.Label>
                                <Form.Control
                                  as='textarea'
                                  rows={2}
                                  placeholder={`${key} ${
                                    isRequiredField ? "*" : ""
                                  }`}
                                  value={value}
                                  onChange={(e) => {
                                    handleInputChange(
                                      section,
                                      key,
                                      e.target.value
                                    )
                                    if (validationErrors[`${section}.${key}`]) {
                                      setValidationErrors((prev) => ({
                                        ...prev,
                                        [`${section}.${key}`]: undefined,
                                      }))
                                    }
                                  }}
                                  isInvalid={
                                    !!validationErrors[`${section}.${key}`]
                                  }
                                />
                                {validationErrors[`${section}.${key}`] && (
                                  <Form.Control.Feedback type='invalid'>
                                    {validationErrors[`${section}.${key}`]}
                                  </Form.Control.Feedback>
                                )}
                              </Form.Group>
                            )
                          } else if (key === "link") {
                            return (
                              <Form.Group key={key} className='mb-3'>
                                <Form.Label className='text-capitalize'>
                                  {key} (Optional)
                                </Form.Label>
                                <Form.Control
                                  type='url'
                                  placeholder='/sites/site-name'
                                  value={value}
                                  onChange={(e) =>
                                    handleInputChange(section, key, e.target.value)
                                  }
                                />
                              </Form.Group>
                            )
                          } else {
                            return (
                              <Form.Group key={key} className='mb-3'>
                                <Form.Label className='text-capitalize'>
                                  {key}{" "}
                                  {isRequiredField && (
                                    <span className='text-danger'>*</span>
                                  )}
                                </Form.Label>
                                <Form.Control
                                  type='text'
                                  placeholder={`${key} ${
                                    isRequiredField ? "*" : ""
                                  }`}
                                  value={value}
                                  onChange={(e) => {
                                    handleInputChange(
                                      section,
                                      key,
                                      e.target.value
                                    )
                                    if (validationErrors[`${section}.${key}`]) {
                                      setValidationErrors((prev) => ({
                                        ...prev,
                                        [`${section}.${key}`]: undefined,
                                      }))
                                    }
                                  }}
                                  isInvalid={
                                    !!validationErrors[`${section}.${key}`]
                                  }
                                />
                                {validationErrors[`${section}.${key}`] && (
                                  <Form.Control.Feedback type='invalid'>
                                    {validationErrors[`${section}.${key}`]}
                                  </Form.Control.Feedback>
                                )}
                              </Form.Group>
                            )
                          }
                        })
                      )}
                    </>
                  </Card.Body>
                )}
              </Card>
            ))}

            <div className='d-flex justify-content-end gap-2'>
              <Button
                variant='secondary'
                onClick={() => setShowFormView(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveChanges}
                disabled={isUpdating || isCreating}
              >
                {isUpdating || isCreating ? (
                  <>
                    <Spinner animation='border' size='sm' className='me-2' />
                    {editItem ? "Saving..." : "Adding..."}
                  </>
                ) : editItem ? (
                  "Save Changes"
                ) : (
                  "Add Content"
                )}
              </Button>
            </div>
          </Form>
        </div>
      )}

      <DeleteModal
        show={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
        isLoading={modalAction === 'delete' ? isDeleting : isToggling}
        title={modalAction === 'delete' ? 'Delete Content' : 'Toggle Status'}
        confirmText={modalAction === 'delete' ? 'Delete Content' : 'Yes'}
        message={
          selectedItem
            ? modalAction === 'delete'
              ? `Are you sure you want to delete "${selectedItem.pageName}"?`
              : `Are you sure you want to ${selectedItem.isActive ? 'deactivate' : 'activate'} "${selectedItem.pageName}"?`
            : `Are you sure you want to ${modalAction === 'delete' ? 'delete' : 'toggle status of'} this item?`
        }
      />
    </div>
  )
}

export default ByInterest
